import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50 shadow-sm"
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg">
              <i className="ri-database-2-line text-lg"></i>
            </div>
            <span className="font-heading font-bold text-lg md:text-xl whitespace-nowrap">
              ACBuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line text-base"></i>
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 whitespace-nowrap"
                      >
                        <span className="w-7 h-7 flex items-center justify-center bg-background-100 rounded-md">
                          <i className={`${cat.icon} text-sm text-primary-500`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/shopping-guide"
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-guide-line text-base"></i>
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              acbuy spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="ml-1 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line ml-1.5"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-foreground-900 rounded-lg cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <i className="ri-home-4-line text-primary-500 text-lg"></i>
              Home
            </Link>

            <Link
              to="/shopping-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <i className="ri-guide-line text-primary-500 text-lg"></i>
              Shopping Guide
            </Link>

            <div className="border-t border-background-200 my-2"></div>

            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 py-2">
              Browse Categories
            </p>

            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex flex-col items-center gap-1.5 px-2 py-3 text-xs text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150 text-center"
                >
                  <span className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className={`${cat.icon} text-sm text-primary-600`}></i>
                  </span>
                  <span className="leading-tight">{cat.name.length > 12 ? cat.name.slice(0, 12) + '...' : cat.name}</span>
                </a>
              ))}
            </div>

            <div className="border-t border-background-200 my-2"></div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <i className="ri-external-link-line text-primary-500 text-lg"></i>
              acbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full mt-2 px-4 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all duration-200"
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}